/**
 * LazyLoadGrid (KINDW-99 / FSD PLP-03)
 *
 * Stripped-down product grid used by the Range Detail PLP. Owns its own pagination state via
 * `useLazyProductSearch` (offset-based) and renders the king-living `RangeProductTile`.
 *
 * KINDW-103 / FSD PLP-07 — also owns the active filter state for `<RangeFilterTags>` so the chips
 * and the grid stay in sync (single source of truth, no prop-drilling across siblings).
 *
 * Why a sibling sub-component instead of a flag deep inside `ProductList`?
 *   The legacy `ProductList` (URL pagination + filter drawers + sort + wishlist) is fundamentally
 *   different in shape from the lazy-load grid. Forking the body in a small wrapper keeps the
 *   legacy code path 100% untouched.
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Skeleton as ProductTileSkeleton} from '@salesforce/retail-react-app/app/components/product-tile';
import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils';

import LazyLoadSentinel from '@amblique/king-living/app/components/product-list/lazy-load-sentinel';
import RangeFilterTags from '@amblique/king-living/app/components/range-filter-tags';
import {toggleRefineValue} from '@amblique/king-living/app/components/range-filter-tags/refine-utils';
import RangeProductTile from '@amblique/king-living/app/components/range-product-tile';
import {VIEW_ITEM_LIST, useGa4TileSelect, useGa4ViewItemList} from '@amblique/king-living/app/analytics/ga4';
import {RANGE_DETAIL_PLP_HEADING_ID, RANGE_PLP_PAGE_SIZE} from '@amblique/king-living/app/constants';
import useLazyProductSearch from '@amblique/king-living/app/hooks/use-lazy-product-search';
import {useMergeSearchHitShortDescriptions} from '@amblique/king-living/app/hooks/use-merge-search-hit-short-descriptions';

const EMPTY_LIST = Object.freeze([]);

/**
 * @param {object} props
 * @param {object} [props.category]              — SFCC category from page-context.
 * @param {number} [props.pageSize]              — items per lazy batch.
 * @param {string[]} [props.filterTagAttributeIds] — KINDW-103: Contentful whitelist of refinement
 *                                                  attribute IDs to expose as click chips.
 * @param {string} [props.contentfulTitle]       — Optional title authored on the Contentful
 *                                                  `ProductListing - Dynamic` entry. Falls back to
 *                                                  `category.name` when empty/whitespace.
 */
export const LazyLoadGrid = ({
  category,
  pageSize = RANGE_PLP_PAGE_SIZE,
  filterTagAttributeIds = EMPTY_LIST,
  contentfulTitle,
  ...rest
}) => {
  const intl = useIntl();
  const einstein = useEinstein();
  const {site} = useMultiSite();
  const styles = useMultiStyleConfig('LazyLoadGrid', {});

  const categoryId = category?.id;

  // KINDW-103: site preference kill-switch. When false the tag bar never renders regardless of
  // Contentful configuration. The value is sourced from the SFCC custom site preference
  // `rangePLPFilterTagsEnabled`, exposed to MRT via the whitelist in
  // `sfra/cartridges/king_living/cartridge/config/mrtClientData.js#preferences` and surfaced on
  // `site.preferences` by `pwa/config/default.js`. Flipping it ON in BM is enough — no env var edit.
  const filterTagsEnabledPref = !!site?.preferences?.rangePLPFilterTagsEnabled;
  const filterTagsEnabled =
    filterTagsEnabledPref &&
    Array.isArray(filterTagAttributeIds) &&
    filterTagAttributeIds.length > 0;

  // Active refine state lives here — `RangeFilterTags` is presentational and `useLazyProductSearch`
  // re-fetches whenever this list changes (signature comparison, see hook).
  const [activeRefine, setActiveRefine] = useState(EMPTY_LIST);

  // KINDW-1572: retain human-readable labels when SFCC drops refinements on zero-hit combos.
  const refinementLabelCacheRef = useRef({
    attributes: new Map(),
    values: new Map(),
  });
  const [refinementLabels, setRefinementLabels] = useState(refinementLabelCacheRef.current);

  const handleToggleFilter = useCallback((attributeId, value) => {
    setActiveRefine((prev) => toggleRefineValue(prev, attributeId, value));
  }, []);

  const {
    hits,
    total,
    refinements,
    hasMore,
    isInitialLoading,
    isRefining,
    isLoadingMore,
    error,
    loadMore,
  } = useLazyProductSearch({
    categoryId,
    pageSize,
    extraRefine: filterTagsEnabled ? activeRefine : EMPTY_LIST,
  });

  const tileProducts = useMergeSearchHitShortDescriptions(hits);
  const isHydratingTiles = hits.length > 0 && tileProducts.length < hits.length;

  const {trackNewProducts, resetTracker} = useGa4ViewItemList(VIEW_ITEM_LIST.PLP);
  const prevHitsLengthRef = useRef(0);
  const activeRefineKey = activeRefine.join('|');

  useEffect(() => {
    resetTracker();
    prevHitsLengthRef.current = 0;
  }, [categoryId, activeRefineKey, resetTracker]);

  useEffect(() => {
    if (isInitialLoading || isRefining || isHydratingTiles || !tileProducts.length) {
      return;
    }

    const prevLength = prevHitsLengthRef.current;
    const batch = tileProducts.slice(prevLength);

    if (!batch.length) {
      return;
    }

    void trackNewProducts(batch, {startIndex: prevLength});
    prevHitsLengthRef.current = tileProducts.length;
  }, [tileProducts, isInitialLoading, isRefining, isHydratingTiles, trackNewProducts]);

  useEffect(() => {
    if (!filterTagsEnabled || !Array.isArray(refinements) || refinements.length === 0) return;

    const cache = refinementLabelCacheRef.current;
    let changed = false;

    for (const group of refinements) {
      const attributeId = group?.attributeId;
      if (!attributeId) continue;

      if (group.label && cache.attributes.get(attributeId) !== group.label) {
        cache.attributes.set(attributeId, group.label);
        changed = true;
      }

      for (const refinementValue of group.values ?? []) {
        if (refinementValue?.value == null) continue;
        const valueKey = `${attributeId}:${refinementValue.value}`;
        const label = refinementValue.label || String(refinementValue.value);
        if (cache.values.get(valueKey) !== label) {
          cache.values.set(valueKey, label);
          changed = true;
        }
      }
    }

    if (changed) {
      setRefinementLabels({
        attributes: new Map(cache.attributes),
        values: new Map(cache.values),
      });
    }
  }, [filterTagsEnabled, refinements]);

  const handleTileClick = useGa4TileSelect(
    VIEW_ITEM_LIST.PLP,
    useCallback(
      (productSearchItem) => {
        if (!category) return;

        try {
          einstein.sendClickCategory(category, productSearchItem);
        } catch {
          // Telemetry failures must not break tile navigation.
        }
      },
      [category, einstein]
    )
  );

  // Resolve the PLP heading: Contentful-authored title wins, fall back to the SFCC category name.
  // We treat whitespace-only Contentful entries as "no title" so an accidental blank field doesn't
  // suppress the category name fallback.
  const resolvedTitle =
    (typeof contentfulTitle === 'string' && contentfulTitle.trim()) ||
    category?.name ||
    '';

  const heading = resolvedTitle ? (
    <Heading
      data-testid="range-plp-heading"
      id={RANGE_DETAIL_PLP_HEADING_ID}
      {...styles.heading}
    >
      {resolvedTitle}
    </Heading>
  ) : null;

  const tagBar = filterTagsEnabled ? (
    <RangeFilterTags
      refinements={refinements}
      whitelist={filterTagAttributeIds}
      activeRefine={activeRefine}
      refinementLabels={refinementLabels}
      onToggle={handleToggleFilter}
    />
  ) : null;

  const showInitialTileSkeleton =
    isInitialLoading ||
    isRefining ||
    (hits.length > 0 && isHydratingTiles && tileProducts.length === 0);

  const pendingTileSkeletonCount = isHydratingTiles ?
    hits.length - tileProducts.length :
    0;

  // Initial-load skeleton: render an entire row of placeholder tiles to keep the page height stable.
  // Also render skeletons while a chip toggle is forcing a refetch (KINDW-103) or while the first
  // getProducts hydrate is in flight so tiles never flash search-sourced Cylindo images.
  if (
    showInitialTileSkeleton ||
    (!hits.length && !error && isHydrated() && activeRefine.length === 0)
  ) {
    return (
      <Box data-testid="sf-product-list-lazy-grid" {...styles.container} {...rest}>
        {heading}
        {tagBar}
        <SimpleGrid {...styles.productGrid}>
          {new Array(pageSize).fill(0).map((_, i) => (
            <ProductTileSkeleton key={`init-skel-${i}`} />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (error && !hits.length) {
    return (
      <Box data-testid="sf-product-list-lazy-grid-error" {...styles.container}>
        {heading}
        {tagBar}
        <Text {...styles.errorText}>
          <FormattedMessage
            defaultMessage="We couldn't load products right now. Please refresh and try again."
            id="range_product_list.error.load_failed"
          />
        </Text>
      </Box>
    );
  }

  return (
    <Box data-testid="sf-product-list-lazy-grid" {...styles.container} {...rest}>
      {heading}
      {tagBar}

      {/* Empty state when filters reduce results to zero (FSD PLP-07: still show the chips so the
          user can recover by toggling them off). */}
      {hits.length === 0 && activeRefine.length > 0 && !isLoadingMore ? (
        <Box {...styles.emptyStateWrapper}>
          <Text data-testid="range-product-list-no-results" {...styles.emptyStateText}>
            <FormattedMessage
              defaultMessage="No products match the selected filters. Try removing one to see more."
              id="range_product_list.empty.filtered"
            />
          </Text>
        </Box>
      ) : (
        <SimpleGrid {...styles.productGrid}>
          {tileProducts.map((product, index) => (
            <RangeProductTile
              key={product.productId ?? product.id}
              data-testid={`range-product-tile-${product.productId ?? product.id}`}
              product={product}
              showBazaarvoiceRating
              showPromoCallout
              tileIndex={index}
              onClick={() => handleTileClick(product, index)}
            />
          ))}

          {(isLoadingMore || pendingTileSkeletonCount > 0) &&
            new Array(
              isLoadingMore ?
                Math.min(pageSize, Math.max(total - hits.length, 0)) :
                pendingTileSkeletonCount
            )
              .fill(0)
              .map((_, i) => <ProductTileSkeleton key={`load-more-skel-${i}`} />)}
        </SimpleGrid>
      )}

      <Flex {...styles.sentinelFooter}>
        {hasMore ? (
          <LazyLoadSentinel enabled={hasMore && !isLoadingMore} onIntersect={loadMore} />
        ) : (
          hits.length > 0 && (
            <Text
              data-testid="range-product-list-end"
              aria-live="polite"
              {...styles.endOfResultsText}
              aria-label={intl.formatMessage(
                {
                  defaultMessage: 'Showing {count} of {total} products.',
                  id: 'range_product_list.live.showing',
                },
                {count: hits.length, total}
              )}
            >
              <FormattedMessage
                defaultMessage="Showing all {total} products"
                id="range_product_list.end_of_results"
                values={{total}}
              />
            </Text>
          )
        )}
      </Flex>
    </Box>
  );
};

LazyLoadGrid.propTypes = {
  category: PropTypes.object,
  pageSize: PropTypes.number,
  filterTagAttributeIds: PropTypes.arrayOf(PropTypes.string),
  contentfulTitle: PropTypes.string,
};

export default LazyLoadGrid;
